/**
 * The result lock's live state: does this player owe a score right now?
 *
 * Backed by GET /match/active — the same endpoint the RESULT tab reads. Server
 * side, deliberately: the lock has to survive a reload, a killed PWA, and a
 * player opening the app on a different device to the one they played on.
 * Anything remembered locally would let them shed the obligation by clearing
 * their browser, which is precisely the behaviour the lock exists to stop.
 *
 * Polled rather than fetched once, because the lock must CLEAR by itself: the
 * opponent's submission, the 5-minute auto-confirm, or a void all end the
 * obligation without this player doing anything, and a stale lock is a trap.
 */
import { useCallback, useEffect, useState } from "react";
import { matchApi } from "./matchApi";
import { resultOwed } from "./centreLogic";
import type { LockKind } from "./resultLock";

/** Slow — this is a background obligation check, not a live surface. It also
 *  runs on focus, which is when it actually matters. */
export const RESULT_LOCK_POLL_MS = 20_000;

export interface ResultLockState {
  /** The match still owing something from this player, or null. */
  owedMatchId: string | null;
  /** Which obligation it is — decides the prompt's copy and its button. */
  kind: LockKind;
  /** False until the first answer lands. NOTHING locks or bounces before this. */
  loaded: boolean;
}

export function useResultLock(enabled: boolean): ResultLockState {
  const [state, setState] = useState<ResultLockState>({
    owedMatchId: null,
    kind: "submit",
    loaded: false,
  });

  const check = useCallback(async () => {
    if (!enabled) return;
    try {
      const res = await matchApi.getActiveMatch();
      // TWO obligations, not one. Owing your own score, and owing a verdict on
      // theirs — the second settles by itself in 5 minutes, so it is the more
      // urgent of the two, and it is checked first.
      const awaitingVerify = res.match !== null && res.awaiting_your_verify;
      const owesScore =
        res.match !== null &&
        !res.you_submitted &&
        resultOwed(res.match.state, null, res.you_arrived);

      if (awaitingVerify) {
        setState({ owedMatchId: res.match?.id ?? null, kind: "verify", loaded: true });
      } else {
        setState({
          owedMatchId: owesScore ? (res.match?.id ?? null) : null,
          kind: "submit",
          loaded: true,
        });
      }
    } catch {
      // FAIL OPEN. An unreachable engine leaves `loaded` false, so the lock
      // never engages. Being offline must not also mean being unable to move
      // around the app — the obligation is still there when the server is back.
      setState((s) => (s.loaded ? s : { owedMatchId: null, kind: "submit", loaded: false }));
    }
  }, [enabled]);

  useEffect(() => {
    if (!enabled) {
      setState({ owedMatchId: null, kind: "submit", loaded: false });
      return;
    }
    void check();
    const timer = setInterval(() => void check(), RESULT_LOCK_POLL_MS);
    // On focus too: coming back to the app is exactly the moment a player who
    // wandered off mid-match needs to be caught.
    const onFocus = () => void check();
    window.addEventListener("focus", onFocus);
    return () => {
      clearInterval(timer);
      window.removeEventListener("focus", onFocus);
    };
  }, [enabled, check]);

  return state;
}
